// Correção UX v2 §5.2.3 — autocomplete de slash commands COMPARTILHADO entre os
// composers (CompanionPage + SessionsPage). Extraído do CompanionPage (PR4) com
// comportamento idêntico + navegação por teclado:
//   - `/` no início do input abre o menu (GET /v1/slash-commands via
//     api.listSlashCommands, refetch em window focus + realtime em slash_commands)
//   - ↑/↓ navegam, Enter seleciona, Esc fecha (até o input mudar)
//   - clique também seleciona; substituição inline → onPick recebe "/slug "
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { api } from '@/lib/api';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { cn } from '@/lib/utils';

export interface SlashCommandSuggestion {
  slug: string;
  name: string;
  description: string | null;
}

interface SlashAutocompleteProps {
  /** Texto atual do composer (controlado pelo pai). */
  input: string;
  /** Substituição inline — recebe o novo valor do input (ex.: "/status "). */
  onPick: (value: string) => void;
  /** Ref do <input> do composer — alvo da navegação ↑↓/Enter/Esc. */
  inputRef: RefObject<HTMLInputElement | null>;
  /** Scope do GET /v1/slash-commands (default: dashboard). */
  scope?: string;
  /** Overrides de posicionamento do overlay (default: colado no composer). */
  className?: string;
}

export function SlashAutocomplete({
  input,
  onPick,
  inputRef,
  scope = 'dashboard',
  className,
}: SlashAutocompleteProps) {
  const [commands, setCommands] = useState<SlashCommandSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Esc fecha o menu PARA o input atual — input mudou → reabre (sem effect).
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  // Realtime — qualquer INSERT/UPDATE/DELETE em slash_commands dispara refetch
  // (bump no tick re-roda o effect de load). RLS já filtra por tenant_id NULL
  // OR == jwt.tenant_id.
  const [refreshTick, setRefreshTick] = useState(0);
  useSupabaseRealtime({ table: 'slash_commands' }, () => {
    setRefreshTick((t) => t + 1);
  });

  // Carrega lista de slashes (mount + window focus + realtime via refreshTick).
  // Garante que o autocomplete sempre reflete o estado atual do DB —
  // criar/editar/deletar slash via /admin/slash-commands aparece imediato.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // api.request já desencapsula o envelope { success, data } e retorna o
        // array direto (regressão histórica: cast `(res as { data }).data`
        // gerava undefined → autocomplete nunca aparecia).
        const data = await api.listSlashCommands(scope);
        if (!cancelled) setCommands(data ?? []);
      } catch {
        // Best-effort — sem slash autocomplete se endpoint indisponível
      }
    };
    void load();
    const onFocus = () => {
      void load();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [scope, refreshTick]);

  // Filtra commands conforme o user digita após `/` (mesma lógica do PR4).
  const suggestions = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const query = input.slice(1).toLowerCase();
    if (query.length === 0) return commands.slice(0, 8);
    return commands
      .filter((c) => c.slug.toLowerCase().startsWith(query) || c.slug.toLowerCase().includes(query))
      .slice(0, 8);
  }, [input, commands]);

  // Seleção derivada com clamp — lista mudou/encolheu → índice sempre válido
  // (sem setState em effect, regra react-hooks/set-state-in-effect).
  const effectiveIndex = suggestions.length > 0 ? Math.min(activeIndex, suggestions.length - 1) : 0;

  const open = dismissedFor !== input && input.startsWith('/') && suggestions.length > 0;

  const pick = useCallback(
    (cmd: SlashCommandSuggestion) => {
      onPick(`/${cmd.slug} `);
      inputRef.current?.focus();
    },
    [onPick, inputRef],
  );

  // Estado mais recente acessível pelo listener nativo (registrado 1x por
  // elemento) — ref sincronizada via effect (não no render, regra react-hooks/refs).
  const stateRef = useRef({ open, suggestions, effectiveIndex, pick, input });
  useEffect(() => {
    stateRef.current = { open, suggestions, effectiveIndex, pick, input };
  }, [open, suggestions, effectiveIndex, pick, input]);

  // Navegação por teclado direto no <input> do composer. Listener NATIVO no
  // elemento: dispara antes dos handlers delegados do React (root), então
  // Enter com o menu aberto seleciona o comando SEM submeter o form.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const s = stateRef.current;
      if (!s.open || s.suggestions.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((s.effectiveIndex + 1) % s.suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((s.effectiveIndex - 1 + s.suggestions.length) % s.suggestions.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        s.pick(s.suggestions[s.effectiveIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setDismissedFor(s.input);
      }
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
    // `open` na dep cobre composer montado tardiamente (ex.: SessionsPage só
    // renderiza o input com sessão ativa) — re-attach quando o menu abre.
  }, [inputRef, open]);

  if (!open) return null;

  return (
    <div
      role="listbox"
      aria-label="Comandos slash"
      className={cn(
        'absolute bottom-full left-3 right-3 z-20 mb-1 max-h-72 overflow-auto rounded-md border border-raios-border bg-raios-surface shadow-lg',
        className,
      )}
    >
      {suggestions.map((cmd, i) => (
        <button
          key={cmd.slug}
          type="button"
          role="option"
          aria-selected={i === effectiveIndex}
          className={cn(
            'flex w-full items-start gap-2 border-b border-raios-border/60 px-3 py-2 text-left hover:bg-raios-surface-hover cursor-pointer',
            i === effectiveIndex && 'bg-raios-surface-hover',
          )}
          onMouseEnter={() => setActiveIndex(i)}
          onClick={() => pick(cmd)}
        >
          <code className="font-mono text-sm text-raios-brand">/{cmd.slug}</code>
          <span className="flex-1">
            <span className="block text-sm">{cmd.name}</span>
            {cmd.description && (
              <span className="block text-xs text-raios-muted">{cmd.description}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}
